<template>
  <div
    class="product-card-sawmill"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
    }"
  >
    <a
      :href="product.url"
      class="product-card-sawmill__content"
    >
      <div
        v-if="product.compatibility"
        class="
          product-card-sawmill__compatibility
          text-other-label-l
        "
        v-html="sanitizeCompatibility(product.compatibility)"
      />

      <div class="product-card-sawmill__header">
        <h3
          class="
          product-card-sawmill__title
          text-heading-2xl
          text-heading-xl-desktop
        "
          v-text="product.title"
        />
        <div
          v-if="hasBadge && currentCountry == 'US' || currentCountry == 'CA'"
          class="main-product-card-sawmill__badges"
        >
          <product-badge
            v-if="sale "
            :text="$string('collection.badge.sale')"
            :badge-class="productBadgeClass($string('collection.badge.sale'))"
          />
          <product-badge
            v-else-if="badges && badges.length > 0"
            :text="productBadgeText(badges[0])"
            :badge-class="productBadgeClass(badges[0])"
          />
        </div>
      </div>

      <div class="product-card-sawmill__subheader mt-xs">
        <p
          class="product-card-sawmill__sku text-body-xs-regular"
          v-text="product.sku"
        />

        <product-rating :sku="product.sku" />
      </div>

      <div
        class="product-card-sawmill__description mt-m pt-l"
        v-html="product.description"
      />

      <div class="product-card-sawmill__specs">
        <product-stat
          :label="$string('compare.log_diameter')"
          :value="product.logDiameter"
          :unit="$string('compare.log_diameter_unit')"
        >
          <template #icon>
            <diameter />
          </template>
        </product-stat>

        <product-stat
          :label="$string('compare.width_of_cut')"
          :value="product.widthOfCut"
          :unit="$string('compare.width_of_cut_unit')"
        >
          <template #icon>
            <width />
          </template>
        </product-stat>

        <product-stat
          :label="$string('compare.max_horsepower')"
          :value="product.maxHorsepower"
          :unit="$string('compare.max_horsepower_unit')"
        >
          <template #icon>
            <power />
          </template>
        </product-stat>

        <product-range
          :label="$string('compare.customizability')"
          :value="product.customizability"
        >
          <template #icon>
            <icon-customizability />
          </template>
        </product-range>
      </div>

      <product-prices
        class="product-card-sawmill__prices"
        class-price="text-heading-l"
        class-compare-at-price="text-heading-l"
        :available="product?.available"
        :compare-at-price="product?.compareAtPrice?.amount"
        :price="product?.price?.amount"
        :affirm=true
        sawmill="product-card-sawmill__prices"
      />

      <btn
        class="product-card-sawmill__add-to-cart"
        :label="$string('product.cta.shop_now')"
        :url="product.url"
        modifiers="fill center"
        hover-only
        type="button"
      >
        <template #hoverIcon>
          <icon-cart />
        </template>
      </btn>
    </a>
    <div class="product-card-sawmill__image-container">
      <div
        v-if="product.featuredImage"
        class="product-card-sawmill__primary-image"
      >
        <splide
          :has-track="false"
          :options="config"
        >
          <splide-track>
            <splide-slide
              v-for="(image, index) of product.images"
              :key="`image-${index}`"
            >
              <a
                :href="product.url"
              >
                <responsive-image
                  :image="image"
                  min-max="300-1200"
                  ratio="16:9"
                  @image-loaded="showSecondaryImage = true"
                />
              </a>
            </splide-slide>
          </splide-track>
          <div class="splide__arrows">
            <button
              type="button"
              class="splide__arrow splide__arrow--prev"
            >
              <arrow-left v-if="!isMobile" />
              <chevron-left v-if="isMobile" />
            </button>
            <button
              type="button"
              class="splide__arrow splide__arrow--next"
            >
              <arrow-right v-if="!isMobile" />
              <chevron-right v-if="isMobile" />
            </button>
          </div>
        </splide>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card sawmill
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import ProductBadge from '~async/product-badge/product-badge.vue'
import ProductStat from '~global/product-stat/product-stat'
import ProductRange from '~global/product-range/product-range'
import Btn from '~global/btn/btn'
import ProductRating from '~async/product-rating/product-rating'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import IconCart from '~icons/button/cart.svg'
import ChevronLeft from '~icons/directional-navigation/chevron/left.svg'
import ChevronRight from '~icons/directional-navigation/chevron/right.svg'
import ArrowRight from '~icons/directional-navigation/arrow/right.svg'
import ArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import Diameter from '~icons/spec/diameter.svg'
import Power from '~icons/spec/power.svg'
import Width from '~icons/spec/width.svg'
import IconCustomizability from '~icons/spec/customizability.svg'

export default {
  name: 'ProductCardSawmill',

  components: {
    ProductPrices,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    Btn,
    IconCart,
    ArrowLeft,
    ArrowRight,
    ChevronLeft,
    ChevronRight,
    ProductStat,
    ProductRange,
    Diameter,
    Power,
    Width,
    IconCustomizability,
    ProductBadge,
    ProductRating,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    labels: {
      type: Object,
      default: () => ({
        new: '',
        sale: '',
        most_popular: '',
        best_seller: '',
        freeShipping: '',
      }),
    },
  },

  emits: {
    'product-card-loaded': () => true,
  },

  data() {
    return {
      config: {
        arrows: true,
        pagination: false,
      },
      loadSecondaryImage: false,
      showSecondaryImage: false,
      sale: false,
      currentCountry: `${cnvs.store.country}`,
      hasBadge: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),
  },

  watch: {

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      this.getTruncateLimit(this.isMobile)
    },
  },

  mounted() {
    this.getProductBadges()
    this.emitState()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    emitState() {
      this.$emit('product-card-loaded')
    },

    sanitizeCompatibility(values) {
      let output = ``
      let truncated = false
      const products = values.split('|')
      const limit = this.getTruncateLimit(this.isMobile)

      products.forEach((product, index) => {

        if (index < limit) {
          output += `<span class="product-card-sawmill__compatibility-product">${product}</span>`
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!truncated) {
            output += `<span class="product-card-sawmill__compatibility-product">+${products.length - index}</span>`
            truncated = true
          }
        }
      })

      return output
    },
    getTruncateLimit(isMobile) {
      if (isMobile) {
        return 3
      }
      return 4
    },
    getProductBadge() {
      const { product } = this
      const sale = product.price.amount < product.compareAtPrice.amount
      const tags = product.tags ? product.tags.find((tag) => tag.includes('badge:')) : false
      const badges = product.badge ? product.badge[0] : false
      const badge = tags || badges
      this.sale = sale

      if (!sale && !badge) {
        return
      }

      this.badge = sale ? this.$string('collection.badge.sale') : badge.replace('badge:', '')
    },
    getProductBadges() {
      if (!this.product && !this.liquid) {
        return
      }

      const { product } = this
      const badges = product.tags?.filter((tag) => tag.includes('badge:'))
      const sale = product.compareAtPrice.amount
        ? product.price?.amount < product.compareAtPrice?.amount
        : false

      if ((!badges || badges.length <= 0) && !sale) {
        return
      }
      const badgeTitle = badges.map((badge) => badge.replace('badge:', ''))
      this.hasBadge = true
      this.badges = badgeTitle
      this.sale = sale
    },

    /**
     * Generate badge class to get proper badge color
     * @param {String} badge - badge name.
     */
    productBadgeClass(badge) {
      const text = badge.replace('_', '-').replace(' ', '-')
      return `product-badge--${text.toLowerCase()}`
    },

    /**
     * Generate badge text label by checking if it exists as a setting
     * @param {String} badge - badge name.
     */
    productBadgeText(badge) {
      if (badge.includes('_') && this.labels[`${badge}`]) {
        return this.labels[`${badge}`]
      }

      return badge
    },
  },
}
</script>

<style lang="scss">
@import './product-card-sawmill';
</style>
